import { OfflineIndicator } from './OfflineControls';
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Bell,
  Search,
  Newspaper,
  Menu,
  ArrowLeft,
  ArrowUp,
  ListTodo,
  Users,
  Building2,
  DollarSign,
  UserCheck,
  Activity,
  Percent,
  Server,
  Lock,
  FileText,
  Plus,
  BookOpen,
  Route as RouteIcon,
  BarChart3,
  Shield,
  Siren,
  Settings as SettingsIcon,
  User,
  UserPlus,
  Phone,
  RotateCcw,
  Star,
  Zap,
  Info,
  Smartphone
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import NotificationModal from './NotificationModal';
import OrderLookupModal from './OrderLookupModal';
import Toast from './Toast';
import { API_URL } from '../config';
import { hasOpenModalBackEntries } from '../hooks/useModalBackButton';
import { safeFetch } from '../utils/safeFetch';
import NotificationService from '../utils/notificationService';
import { haptic } from '../utils/vibration';
import useToast from '../hooks/useToast';
import { STAGES_CONFIG } from '../utils/stages';
import { canOpenNotificationRoute } from '../utils/notificationNavigation';
import { useInteractionPreferences } from '../contexts/InteractionPreferencesContext';
import { NewYearGarland } from './NewYearSidebarDecorations';

// Конфигурация заголовков для разных разделов
const HEADER_CONFIG = {
  // Корневые разделы - показывают название раздела + меню
  '/': { title: 'Мои заказы', showMenu: true, showBack: false },
  '/my-orders': { title: 'Мои заказы', showMenu: true, showBack: false, icon: 'orders' },
  '/risk-control': { title: 'Контроль рисков', showMenu: true, showBack: false, icon: 'riskControl' },
  '/new-order': { title: 'Новый заказ', showMenu: true, showBack: false, icon: 'newOrder' },
  '/tasks': { title: 'Поручения', showMenu: true, showBack: false, icon: 'tasks' },
  '/tasks/new': { title: 'Новое поручение', showMenu: false, showBack: true, icon: 'tasks' },
  '/orders-log': { title: 'Журнал заказов', showMenu: true, showBack: false, icon: 'log' },
  '/routes': { title: 'Маршруты', showMenu: true, showBack: false, icon: 'routes' },
  '/expenses': { title: 'Расходы', showMenu: true, showBack: false, icon: 'expenses' },
  '/statistics': { title: 'Статистика', showMenu: true, showBack: false, icon: 'stats' },
  '/management': { title: 'Управление', showMenu: true, showBack: false, icon: 'management' },
  '/news': { title: 'Новости', showMenu: true, showBack: false, icon: 'news' },
  '/settings': { title: 'Настройки', showMenu: true, showBack: false, icon: 'settings' },
  '/profile': { title: 'Профиль', showMenu: true, showBack: false, icon: 'profile' },
  
  // Подразделы управления - показывают стрелку назад + название
  '/management/departments': { title: 'Подразделения', showMenu: false, showBack: true, icon: 'building' },
  '/management/services': { title: 'Услуги и цены', showMenu: false, showBack: true, icon: 'dollar' },
  '/management/client-app': { title: 'Клиентское приложение', showMenu: false, showBack: true, icon: 'clientApp' },
  '/management/clients': { title: 'Клиенты', showMenu: false, showBack: true, icon: 'clients' },
  '/management/profit-radar': { title: 'Радар прибыли', showMenu: false, showBack: true, icon: 'stats' },
  '/management/activity': { title: 'Активность', showMenu: false, showBack: true, icon: 'activity' },
  '/management/discounts': { title: 'Скидки', showMenu: false, showBack: true, icon: 'discount' },
  '/management/notifications': { title: 'Уведомления', showMenu: false, showBack: true, icon: 'bell' },
  '/management/news': { title: 'Публикации', showMenu: false, showBack: true, icon: 'news' },
  '/management/system': { title: 'Система', showMenu: false, showBack: true, icon: 'system' },
  '/management/security': { title: 'Безопасность', showMenu: false, showBack: true, icon: 'security' },
  
  // Подразделы расходов
  '/expenses/analytics': { title: 'Аналитика расходов', showMenu: false, showBack: true, icon: 'expenses' },
  
  // Подразделы заказов
  '/order': { title: 'Детали заказа', showMenu: false, showBack: true, icon: 'order', dynamicPrefix: '/order' },
  '/tasks-detail': { title: 'Поручение', showMenu: false, showBack: true, icon: 'tasks', dynamicPrefix: '/tasks/' },
  
  // Создание/редактирование
  '/create-user': { title: 'Создание пользователя', showMenu: false, showBack: true, icon: 'userPlus' },
};

const MANAGEMENT_PARENT_PATHS = new Set([
  '/management/departments',
  '/management/services',
  '/management/client-app',
  '/management/clients',
  '/management/profit-radar',
  '/management/activity',
  '/management/discounts',
  '/management/notifications',
  '/management/news',
  '/management/system',
  '/management/security'
]);

const ROOT_SECTION_PATHS = new Set([
  '/',
  '/my-orders',
  '/tasks',
  '/orders-log',
  '/risk-control',
  '/routes',
  '/management',
  '/expenses',
  '/statistics',
  '/news',
  '/settings',
  '/profile'
]);

const BACK_TRAP_QUERY_PARAM = '__bw_backtrap';
const BACK_TRAP_QUERY_VALUE = '1';

// Иконки для разделов
const SECTION_ICONS = {
  orders: FileText,
  newOrder: Plus,
  tasks: ListTodo,
  log: BookOpen,
  routes: RouteIcon,
  expenses: DollarSign,
  stats: BarChart3,
  management: Shield,
  settings: SettingsIcon,
  profile: User,
  riskControl: Shield,
  users: Users,
  building: Building2,
  dollar: DollarSign,
  clientApp: Smartphone,
  clients: UserCheck,
  activity: Activity,
  discount: Percent,
  bell: Bell,
  news: Newspaper,
  system: Server,
  security: Lock,
  order: FileText,
  userPlus: UserPlus,
};

const copyTextToClipboard = async (value) => {
  const text = String(value || '').trim();
  if (!text) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.warn('Clipboard API недоступен, пробуем fallback:', error);
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch (err) {
    console.warn('Fallback copy failed:', err);
    return false;
  }
};

const Header = ({ setSidebarOpen, newYearInterfaceEnabled = false }) => {
  const { pushVibrationEnabled, browserNotificationsEnabled } = useInteractionPreferences();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isOrderLookupModalOpen, setIsOrderLookupModalOpen] = useState(false);
  const [, setWebsocket] = useState(null);
  const [, setUserId] = useState(null);
  const [orderHeaderTitle, setOrderHeaderTitle] = useState('');
  const [orderHeaderNumber, setOrderHeaderNumber] = useState('');
  const [orderHeaderUrgent, setOrderHeaderUrgent] = useState(false);
  const [orderHeaderEmergency, setOrderHeaderEmergency] = useState(false);
  const [orderHeaderClientVip, setOrderHeaderClientVip] = useState(false);
  const [orderHeaderDispatcherPriority, setOrderHeaderDispatcherPriority] = useState(false);
  const [orderHeaderDispatcherCallStage, setOrderHeaderDispatcherCallStage] = useState('');
  const [orderHeaderRework, setOrderHeaderRework] = useState(false);
  const [activeOrderHeaderBadgeHint, setActiveOrderHeaderBadgeHint] = useState('');
  const [orderHeaderBadgeHintStyle, setOrderHeaderBadgeHintStyle] = useState(null);
  const [taskHeaderTitle, setTaskHeaderTitle] = useState('');
  const [dispatcherModeActive, setDispatcherModeActive] = useState(false);
  const { toast, showToast, hideToast } = useToast(3000);
  const pollingIntervalRef = useRef(null);
  const websocketAliveRef = useRef(false);
  const websocketRef = useRef(null);
  const pushVibrationEnabledRef = useRef(pushVibrationEnabled);
  const browserNotificationsEnabledRef = useRef(browserNotificationsEnabled);
  const orderHeaderBadgeHintTimeoutRef = useRef(null);
  const orderHeaderBadgeRefs = useRef({});
  const orderTitleLongPressTimerRef = useRef(null);
  const orderTitleLongPressTriggeredRef = useRef(false);
  const orderTitleTouchStartPosRef = useRef({ x: 0, y: 0 });
  const previousBackTrapBaseRef = useRef('');
  const previousBackTrapActiveRef = useRef(false);
  const lastArmedBackTrapBaseRef = useRef('');

  useEffect(() => {
    pushVibrationEnabledRef.current = pushVibrationEnabled;
  }, [pushVibrationEnabled]);

  useEffect(() => {
    browserNotificationsEnabledRef.current = browserNotificationsEnabled;
  }, [browserNotificationsEnabled]);

  const registerOrderHeaderBadgeRef = useCallback((badgeKey) => (node) => {
    if (node) {
      orderHeaderBadgeRefs.current[badgeKey] = node;
      return;
    }
    delete orderHeaderBadgeRefs.current[badgeKey];
  }, []);

  const hideOrderHeaderBadgeHint = useCallback(() => {
    setActiveOrderHeaderBadgeHint('');
    setOrderHeaderBadgeHintStyle(null);
  }, []);

  const showOrderHeaderBadgeHint = useCallback((badgeKey) => {
    const badgeNode = orderHeaderBadgeRefs.current[badgeKey];
    if (!badgeNode || typeof window === 'undefined') {
      setOrderHeaderBadgeHintStyle(null);
      return;
    }
    const rect = badgeNode.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const maxBubbleWidth = Math.min(180, Math.max(120, viewportWidth - 24));
    const idealLeft = rect.left + rect.width / 2;
    const clampedLeft = Math.min(
      Math.max(idealLeft, maxBubbleWidth / 2 + 12),
      viewportWidth - maxBubbleWidth / 2 - 12
    );
    setOrderHeaderBadgeHintStyle({
      left: `${clampedLeft}px`,
      top: `${rect.bottom + 8}px`,
      maxWidth: `${maxBubbleWidth}px`
    });
  }, []);

  const handleOrderHeaderBadgeTap = useCallback((badgeKey) => {
    haptic('tap');
    setActiveOrderHeaderBadgeHint((prev) => {
      const next = prev === badgeKey ? '' : badgeKey;
      if (orderHeaderBadgeHintTimeoutRef.current) {
        clearTimeout(orderHeaderBadgeHintTimeoutRef.current);
        orderHeaderBadgeHintTimeoutRef.current = null;
      }
      if (!next) {
        setOrderHeaderBadgeHintStyle(null);
      }
      if (next) {
        showOrderHeaderBadgeHint(next);
        orderHeaderBadgeHintTimeoutRef.current = setTimeout(() => {
          hideOrderHeaderBadgeHint();
          orderHeaderBadgeHintTimeoutRef.current = null;
        }, 2200);
      }
      return next;
    });
  }, [hideOrderHeaderBadgeHint, showOrderHeaderBadgeHint]);

  const orderHeaderBadgeButtonClassName = "order-header-badge-button inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border p-0 align-middle text-[0] leading-none appearance-none";

  useEffect(() => (
    () => {
      if (orderHeaderBadgeHintTimeoutRef.current) {
        clearTimeout(orderHeaderBadgeHintTimeoutRef.current);
      }
    }
  ), []);

  const refreshUnreadCount = async () => {
    try {
      const count = await NotificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      console.error('Ошибка загрузки количества уведомлений:', err);
    }
  };

  const startNotificationPolling = () => {
    if (pollingIntervalRef.current) return;
    refreshUnreadCount();
    pollingIntervalRef.current = setInterval(() => {
      if (!websocketAliveRef.current && document.visibilityState === 'visible') {
        refreshUnreadCount();
      }
    }, 30000);
  };

  const stopNotificationPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Функция для проверки наличия открытых модальных окон
  const isModalOpen = () => {
    const hasManagedModal = hasOpenModalBackEntries();
    const hasOverlayModal = Boolean(document.querySelector('.modal-overlay-backdrop'));

    if (!hasManagedModal && !hasOverlayModal && document.body.dataset.modalCount !== '0') {
      document.body.dataset.modalCount = '0';
    }

    return hasManagedModal || hasOverlayModal;
  };

  const handleOpenSidebar = () => {
    // Не открываем боковое меню если открыто модальное окно
    if (isModalOpen()) return;
    haptic('open');
    setSidebarOpen(true);
  };
  const [newOrderStep, setNewOrderStep] = useState(1);

  // Слушаем изменения sessionStorage для пользователей
  const [usersContext, setUsersContext] = useState({ showCreateUser: false, userDetailsLogin: '' });
  useEffect(() => {
    const checkUsersContext = () => {
      setUsersContext({
        showCreateUser: sessionStorage.getItem('showCreateUser') === 'true',
        userDetailsLogin: sessionStorage.getItem('userDetailsLogin') || ''
      });
    };
    checkUsersContext();
    const interval = setInterval(checkUsersContext, 200);
    return () => clearInterval(interval);
  }, []);

  // Слушаем изменения шага нового заказа
  useEffect(() => {
    const handleStepChange = (e) => {
      setNewOrderStep(e.detail);
    };
    window.addEventListener('neworderstep', handleStepChange);
    return () => window.removeEventListener('neworderstep', handleStepChange);
  }, []);

  const [newOrderCartOpen, setNewOrderCartOpen] = useState(false);
  useEffect(() => {
    const handleCartChange = (e) => setNewOrderCartOpen(e.detail);
    window.addEventListener('newordercart', handleCartChange);
    return () => window.removeEventListener('newordercart', handleCartChange);
  }, []);

  useEffect(() => {
    const handleOrderTitleChange = (event) => {
      setOrderHeaderTitle(event.detail?.title || '');
      setOrderHeaderNumber(String(event.detail?.orderNumber || ''));
      setOrderHeaderUrgent(Boolean(event.detail?.urgent));
      setOrderHeaderEmergency(Boolean(event.detail?.emergencyDispatch));
      setOrderHeaderClientVip(Boolean(event.detail?.clientVip));
      setOrderHeaderDispatcherPriority(Boolean(event.detail?.dispatcherPriority));
      setOrderHeaderDispatcherCallStage(String(event.detail?.dispatcherCallRequiredStage || '').trim().toLowerCase());
      setOrderHeaderRework(Boolean(event.detail?.rework));
      hideOrderHeaderBadgeHint();
    };

    window.addEventListener('orderheadertitle', handleOrderTitleChange);
    return () => window.removeEventListener('orderheadertitle', handleOrderTitleChange);
  }, [hideOrderHeaderBadgeHint]);

  useEffect(() => {
    const handleTaskTitleChange = (event) => {
      setTaskHeaderTitle(event.detail?.title || '');
    };

    window.addEventListener('taskheadertitle', handleTaskTitleChange);
    return () => window.removeEventListener('taskheadertitle', handleTaskTitleChange);
  }, []);

  useEffect(() => {
    const handleDispatcherModeChange = (event) => {
      setDispatcherModeActive(Boolean(event.detail?.enabled));
    };

    window.addEventListener('orderdispatchermodechange', handleDispatcherModeChange);
    return () => window.removeEventListener('orderdispatchermodechange', handleDispatcherModeChange);
  }, []);

  const headerMode = useMemo(() => {
    const isOrderDetail = location.pathname.startsWith('/order');
    const isTaskDetail = location.pathname.startsWith('/tasks/');
    if (!isOrderDetail && !isTaskDetail) return null;
    const params = new URLSearchParams(location.search);
    const source = params.get('from') || '';
    const tab = params.get('tab') || '';
    const currentUserType = localStorage.getItem('user_type') || '';
    const isPrivilegedUser = ['dispatcher', 'admin', 'developer'].includes(currentUserType);

    if (dispatcherModeActive && isPrivilegedUser) {
      return {
        label: 'Диспетчерский доступ',
        description: 'Включены расширенные действия старшей роли. Их можно отключить щитом в диспетчерском блоке.',
        icon: Shield,
        surface: 'bg-violet-50/90 dark:bg-violet-950/35',
        tone: 'text-violet-700 dark:text-violet-300',
        infoTone: 'text-violet-500 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-200'
      };
    }

    if (source === 'my-orders' && tab === 'available' && !isPrivilegedUser) {
      return {
        label: 'Режим просмотра',
        description: 'До принятия заказа на себя здесь доступен только просмотр.',
        icon: Lock,
        surface: 'bg-amber-50/90 dark:bg-amber-950/35',
        tone: 'text-amber-700 dark:text-amber-300',
        infoTone: 'text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200'
      };
    }

    if (source === 'my-orders' && tab === 'archive') {
      return {
        label: 'Архивный доступ',
        description: 'Ваш этап по заказу уже завершён. Можно просматривать заказ, добавлять комментарии и фото.',
        icon: BookOpen,
        surface: 'bg-slate-100/90 dark:bg-slate-800/70',
        tone: 'text-slate-600 dark:text-slate-300',
        infoTone: 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200'
      };
    }

    return null;
  }, [dispatcherModeActive, location.pathname, location.search]);

  const myOrdersStageFromUrl = useMemo(() => {
    if (location.pathname !== '/my-orders') return '';
    const params = new URLSearchParams(location.search);
    return params.get('stage') || '';
  }, [location.pathname, location.search]);

  const effectiveMyOrdersStage = myOrdersStageFromUrl;

  const buildMyOrdersTarget = useCallback((tab = 'my', stage = '') => {
    if (!stage) return '/my-orders';
    return `/my-orders?${new URLSearchParams({ tab, stage }).toString()}`;
  }, []);

  const buildNotificationOrderRoute = useCallback((route) => {
    if (!route?.startsWith('/order/')) return route;
    const [pathname, search = ''] = route.split('?');
    const params = new URLSearchParams(search);
    params.delete('from');
    params.delete('tab');
    params.delete('stage');
    params.set('notification', '1');
    return `${pathname}?${params.toString()}`;
  }, []);

  const backBehavior = useMemo(() => {
    if (location.pathname === '/new-order') {
      if (newOrderCartOpen) return { kind: 'event', eventName: 'newordercartclose' };
      if (newOrderStep > 1) return { kind: 'event', eventName: 'neworderback' };
      return { kind: 'sidebar' };
    }

    if (location.pathname === '/my-orders' && effectiveMyOrdersStage) {
      return { kind: 'route', target: '/my-orders' };
    }

    if (ROOT_SECTION_PATHS.has(location.pathname) || (location.pathname === '/my-orders' && !effectiveMyOrdersStage)) {
      return { kind: 'sidebar' };
    }

    if (location.pathname === '/management/users') {
      if (usersContext.showCreateUser) return { kind: 'event', eventName: 'createuserback' };
      if (usersContext.userDetailsLogin) return { kind: 'event', eventName: 'userdetailsback' };
      return { kind: 'route', target: '/management' };
    }

    if (MANAGEMENT_PARENT_PATHS.has(location.pathname)) {
      return { kind: 'route', target: '/management' };
    }

    if (location.pathname === '/expenses/analytics') {
      return { kind: 'route', target: '/expenses' };
    }

    if (location.pathname.startsWith('/order/')) {
      return { kind: 'history' };
    }

    if (location.pathname.startsWith('/tasks/')) {
      return { kind: 'history' };
    }

    return { kind: 'history' };
  }, [
    effectiveMyOrdersStage,
    location.pathname,
    location.search,
    newOrderCartOpen,
    newOrderStep,
    usersContext.showCreateUser,
    usersContext.userDetailsLogin
  ]);

  const executeBackBehavior = useCallback((behavior) => {
    if (!behavior || behavior.kind === 'history') {
      navigate(-1);
      return;
    }

    if (behavior.kind === 'sidebar') {
      if (!isModalOpen()) {
        setSidebarOpen(true);
      }
      return;
    }

    if (behavior.kind === 'route') {
      if (behavior.target.startsWith('/my-orders')) {
        const [, queryString = ''] = behavior.target.split('?');
        const params = new URLSearchParams(queryString);
        const stage = params.get('stage') || '';
        const tab = params.get('tab') || '';

        if (stage) {
          sessionStorage.setItem('myOrdersStage', stage);
        } else {
          sessionStorage.removeItem('myOrdersStage');
        }

        if (tab) {
          sessionStorage.setItem('myOrdersActiveTab', tab);
        } else {
          sessionStorage.removeItem('myOrdersActiveTab');
        }
      }

      navigate(behavior.target, { replace: true });
      return;
    }

    if (behavior.kind === 'event') {
      if (behavior.eventName === 'createuserback') {
        sessionStorage.removeItem('showCreateUser');
      }
      if (behavior.eventName === 'userdetailsback') {
        sessionStorage.removeItem('userDetailsLogin');
      }
      window.dispatchEvent(new CustomEvent(behavior.eventName));
    }
  }, [navigate, setSidebarOpen]);

  useEffect(() => {
    if (!location.pathname.startsWith('/order')) {
      setOrderHeaderTitle('');
      setOrderHeaderNumber('');
      setOrderHeaderUrgent(false);
      setOrderHeaderClientVip(false);
      setOrderHeaderDispatcherPriority(false);
      setOrderHeaderDispatcherCallStage('');
      setOrderHeaderRework(false);
      hideOrderHeaderBadgeHint();
      setDispatcherModeActive(false);
    }
    if (!location.pathname.startsWith('/tasks/')) {
      setTaskHeaderTitle('');
    }
  }, [hideOrderHeaderBadgeHint, location.pathname]);

  // Определение текущего заголовка и настроек шапки
  const getCurrentHeaderConfig = () => {
    const pathname = location.pathname;

    // Если выбран этап в "Мои заказы" - показываем его название и стрелку назад
    if (pathname === '/my-orders' && effectiveMyOrdersStage) {
      const stageConfig = STAGES_CONFIG[effectiveMyOrdersStage];
      return {
        title: stageConfig?.label || 'Этап',
        showMenu: false,
        showBack: true,
        icon: effectiveMyOrdersStage
      };
    }

    // Новый заказ - шаг текущей формы показываем прямо в заголовке
    if (pathname === '/new-order') {
      if (newOrderCartOpen) {
        return {
          title: 'Корзина заказа',
          showMenu: false,
          showBack: true,
          icon: 'newOrder'
        };
      }
      return {
        title: `Новый заказ · ${newOrderStep}/3`,
        showMenu: newOrderStep === 1,
        showBack: newOrderStep > 1,
        icon: 'newOrder'
      };
    }

    // Детали пользователя - показываем логин
    if (pathname === '/management/users') {
      if (usersContext.showCreateUser) {
        return {
          title: 'Создание пользователя',
          showMenu: false,
          showBack: true,
          icon: 'users'
        };
      }
      
      if (usersContext.userDetailsLogin) {
        return {
          title: usersContext.userDetailsLogin,
          showMenu: false,
          showBack: true,
          icon: 'users'
        };
      }
      
      // Список пользователей
      return {
        title: 'Пользователи',
        showMenu: false,
        showBack: true,
        icon: 'users'
      };
    }

    // Проверяем точное совпадение
    if (HEADER_CONFIG[pathname]) {
      return HEADER_CONFIG[pathname];
    }

    // Проверяем динамические префиксы (например, /order/123)
    for (const [, config] of Object.entries(HEADER_CONFIG)) {
      if (config.dynamicPrefix && pathname.startsWith(config.dynamicPrefix)) {
        if (config.dynamicPrefix === '/order') {
          return {
            ...config,
            title: orderHeaderTitle || 'Заказ'
          };
        }
        if (config.dynamicPrefix === '/tasks/') {
          return {
            ...config,
            title: taskHeaderTitle || 'Поручение'
          };
        }
        return config;
      }
    }

    // По умолчанию - показываем "Большая Стирка" + меню
    return { title: 'Большая Стирка', showMenu: true, showBack: false };
  };

  const headerConfig = getCurrentHeaderConfig();

  const cleanLocationSearch = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    params.delete(BACK_TRAP_QUERY_PARAM);
    const value = params.toString();
    return value ? `?${value}` : '';
  }, [location.search]);

  const trapLocationSearch = useMemo(() => {
    const params = new URLSearchParams(cleanLocationSearch || '');
    params.set(BACK_TRAP_QUERY_PARAM, BACK_TRAP_QUERY_VALUE);
    return `?${params.toString()}`;
  }, [cleanLocationSearch]);

  const isBackTrapActive = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get(BACK_TRAP_QUERY_PARAM) === BACK_TRAP_QUERY_VALUE;
  }, [location.search]);

  const baseLocationSignature = useMemo(
    () => `${location.pathname}${cleanLocationSearch}${location.hash || ''}`,
    [cleanLocationSearch, location.hash, location.pathname]
  );

  useLayoutEffect(() => {
    const previousBaseLocation = previousBackTrapBaseRef.current;
    const previousTrapActive = previousBackTrapActiveRef.current;
    const cameFromTrap =
      previousBaseLocation === baseLocationSignature
      && previousTrapActive
      && !isBackTrapActive;

    previousBackTrapBaseRef.current = baseLocationSignature;
    previousBackTrapActiveRef.current = isBackTrapActive;

    if (!backBehavior || backBehavior.kind === 'history') {
      lastArmedBackTrapBaseRef.current = '';
      if (isBackTrapActive) {
        navigate(
          {
            pathname: location.pathname,
            search: cleanLocationSearch,
            hash: location.hash
          },
          {
            replace: true,
            state: location.state
          }
        );
      }
      return undefined;
    }

    if (cameFromTrap) {
      if (backBehavior.kind === 'sidebar') {
        if (!isModalOpen()) {
          setSidebarOpen(true);
        }

        lastArmedBackTrapBaseRef.current = baseLocationSignature;
        navigate(
          {
            pathname: location.pathname,
            search: trapLocationSearch,
            hash: location.hash
          },
          {
            replace: false,
            state: location.state
          }
        );

        return undefined;
      }

      lastArmedBackTrapBaseRef.current = '';
      executeBackBehavior(backBehavior);
      return undefined;
    }

    if (isModalOpen()) {
      return undefined;
    }

    if (isBackTrapActive) {
      lastArmedBackTrapBaseRef.current = baseLocationSignature;
      return undefined;
    }

    if (lastArmedBackTrapBaseRef.current === baseLocationSignature) {
      return undefined;
    }

    lastArmedBackTrapBaseRef.current = baseLocationSignature;
    navigate(
      {
        pathname: location.pathname,
        search: trapLocationSearch,
        hash: location.hash
      },
      {
        replace: false,
        state: location.state
      }
    );

    return undefined;
  }, [
    backBehavior,
    baseLocationSignature,
    cleanLocationSearch,
    executeBackBehavior,
    isBackTrapActive,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    newOrderCartOpen,
    newOrderStep,
    navigate,
    setSidebarOpen,
    trapLocationSearch
  ]);

  // Обработчик кнопки "Назад"
  const handleBack = () => {
    haptic('close');
    if (hasOpenModalBackEntries()) {
      window.history.back();
      return;
    }
    executeBackBehavior(backBehavior);
  };

  // Получение ID пользователя
  const fetchUserId = async () => {
    try {
      const data = await safeFetch(`${API_URL}/api/check-auth`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      console.log('Получены данные пользователя:', data);
      const userId = data.user_id || data.id;
      console.log('Извлеченный userId:', userId);
      
      if (userId) {
        setUserId(userId);
        localStorage.setItem('user_id', userId);
        return userId;
      } else {
        console.error('user_id не найден в ответе API');
        return null;
      }
    } catch (err) {
      console.error('Ошибка получения ID пользователя:', err);
      return null;
    }
  };

  // Получение количества непрочитанных уведомлений
  // Проверка токена
  const checkToken = () => {
    const token = localStorage.getItem('token');
    console.log('Токен авторизации:', token ? 'присутствует' : 'отсутствует');
    return token;
  };

  // Подключение к WebSocket
  useEffect(() => {
    let reconnectTimeout = null;
    let isConnecting = false;

    const initializeWebSocket = async () => {
      // Предотвращаем множественные попытки подключения
      if (isConnecting) return;
      
      isConnecting = true;
      
      // Проверяем токен
      const accessToken = checkToken();
      if (!accessToken) {
        console.error('Токен отсутствует, WebSocket не может подключиться');
        isConnecting = false;
        return;
      }

      const currentUserId = await fetchUserId();
      console.log('Текущий user_id из /api/check-auth:', currentUserId, 'тип:', typeof currentUserId);

      if (currentUserId && currentUserId !== "undefined" && currentUserId !== "null") {
        setUserId(currentUserId);
      }

      const buildWebSocketUrl = () => {
        const explicitApiBase = String(API_URL || '').trim().replace(/\/+$/, '');
        if (explicitApiBase) {
          let wsBase = explicitApiBase.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
          if (explicitApiBase.includes('localhost') && explicitApiBase.startsWith('https://')) {
            wsBase = explicitApiBase.replace(/^https:/, 'ws:');
          }
          return `${wsBase}/api/notifications/ws`;
        }

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}/api/notifications/ws`;
      };

      const wsUrl = buildWebSocketUrl();
      
      console.log('Подключение к WebSocket: защищенный канал уведомлений');
      console.log('API_URL:', API_URL);
      
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;
      
      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'auth', token: accessToken }));
        isConnecting = false;
        // Очищаем таймер повторного подключения
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
          reconnectTimeout = null;
        }
      };
      
      ws.onmessage = (event) => {
        console.log('Получено сообщение WebSocket:', event.data);
        
        // Игнорируем текстовые pong сообщения
        if (event.data === 'pong') {
          return;
        }
        
        // Пытаемся парсить JSON только если это не текстовое сообщение
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (e) {
          console.log('Получено текстовое сообщение:', event.data);
          return;
        }
        
        // Игнорируем служебные сообщения
        if (data.type === 'connection_established') {
          console.log('WebSocket подключение подтверждено');
          websocketAliveRef.current = true;
          stopNotificationPolling();
          refreshUnreadCount();
          const pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send('ping');
            } else {
              clearInterval(pingInterval);
            }
          }, 30000);
          ws.pingInterval = pingInterval;
          return;
        }
        
        // Обрабатываем только уведомления
        if (data.id || (data.title && data.message)) {
          console.log('Получено новое уведомление:', data);
          if (!data.is_read) {
            setUnreadCount(prev => prev + 1);
          }
          setTimeout(() => {
            refreshUnreadCount();
          }, 250);
          // Показываем уведомление в браузере (если поддерживается)
          if (browserNotificationsEnabledRef.current && 'Notification' in window && Notification.permission === 'granted') {
            const browserNotification = new Notification(data.title, {
              body: data.message,
              icon: '/android-chrome-192x192.png?v=20260513',
              vibrate: pushVibrationEnabledRef.current ? [200, 100, 200] : []
            });
            browserNotification.onclick = async () => {
              const access = await canOpenNotificationRoute(data.url);
              if (!access.allowed) {
                browserNotification.close();
                return;
              }
              window.focus();
              navigate(buildNotificationOrderRoute(access.route));
              browserNotification.close();
            };
          }
        }
      };
      
      ws.onclose = () => {
        console.log('WebSocket соединение закрыто для пользователя:', currentUserId);
        isConnecting = false;
        websocketAliveRef.current = false;
        // Очищаем ping интервал
        if (ws.pingInterval) {
          clearInterval(ws.pingInterval);
        }
        startNotificationPolling();
        // Пытаемся переподключиться через 3 секунды
        reconnectTimeout = setTimeout(initializeWebSocket, 3000);
      };
      
      ws.onerror = (error) => {
        console.error('Ошибка WebSocket соединения для пользователя:', currentUserId);
        console.error('Детали ошибки:', error);
        console.error('WebSocket readyState:', ws.readyState);
        console.error('WebSocket URL:', ws.url);
        isConnecting = false;
        websocketAliveRef.current = false;
        startNotificationPolling();
        // Закрываем соединение и пытаемся переподключиться
        ws.close();
      };
      
      setWebsocket(ws);
    };
    
    // Запрашиваем количество непрочитанных уведомлений
    refreshUnreadCount();
    startNotificationPolling();
    
    // Инициализируем WebSocket соединение
    initializeWebSocket();
    
    const handleWindowFocus = () => {
      refreshUnreadCount();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUnreadCount();
      }
    };

    const handleNotificationsChanged = (event) => {
      const detail = event.detail || {};
      if (typeof detail.unreadCount === 'number') {
        setUnreadCount(Math.max(0, detail.unreadCount));
        return;
      }
      if (typeof detail.delta === 'number') {
        setUnreadCount(prev => Math.max(0, prev + detail.delta));
      }
      refreshUnreadCount();
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('notifications:changed', handleNotificationsChanged);
    
    // Очистка при размонтировании компонента
    return () => {
      websocketAliveRef.current = false;
      stopNotificationPolling();
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('notifications:changed', handleNotificationsChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildNotificationOrderRoute, navigate]);

  const handleNotificationClick = () => {
    haptic('open');
    setIsNotificationModalOpen(true);
  };

  const handleOrderLookupClick = () => {
    haptic('open');
    setIsOrderLookupModalOpen(true);
  };

  const handleMarkAllAsRead = () => {
    setUnreadCount(0);
  };

  const handleNotificationRead = () => {
    // Обновляем счетчик после отметки уведомления как прочитанного
    refreshUnreadCount();
  };

  const handleCopyOrderNumber = useCallback(async () => {
    const rawNumber = orderHeaderNumber || (String(orderHeaderTitle || '').match(/\d+/) || [])[0];
    const textToCopy = rawNumber || orderHeaderTitle;
    if (!textToCopy) return;

    haptic('success');
    const copied = await copyTextToClipboard(textToCopy);

    if (copied) {
      showToast(rawNumber ? `Заказ №${rawNumber} скопирован` : `${orderHeaderTitle} скопирован`, 'success');
    } else {
      showToast('Не удалось скопировать номер заказа', 'error');
    }
  }, [orderHeaderNumber, orderHeaderTitle, showToast]);

  const handleOrderTitlePressStart = useCallback((e) => {
    orderTitleLongPressTriggeredRef.current = false;
    const point = e.touches ? e.touches[0] : e;
    orderTitleTouchStartPosRef.current = { x: point.clientX, y: point.clientY };

    if (orderTitleLongPressTimerRef.current) {
      clearTimeout(orderTitleLongPressTimerRef.current);
    }

    orderTitleLongPressTimerRef.current = setTimeout(() => {
      orderTitleLongPressTriggeredRef.current = true;
      handleCopyOrderNumber();
    }, 450);
  }, [handleCopyOrderNumber]);

  const handleOrderTitlePressMove = useCallback((e) => {
    if (!orderTitleLongPressTimerRef.current) return;
    const point = e.touches ? e.touches[0] : e;
    const dx = Math.abs(point.clientX - orderTitleTouchStartPosRef.current.x);
    const dy = Math.abs(point.clientY - orderTitleTouchStartPosRef.current.y);
    if (dx > 10 || dy > 10) {
      clearTimeout(orderTitleLongPressTimerRef.current);
      orderTitleLongPressTimerRef.current = null;
    }
  }, []);

  const handleOrderTitlePressEnd = useCallback((e) => {
    if (orderTitleLongPressTimerRef.current) {
      clearTimeout(orderTitleLongPressTimerRef.current);
      orderTitleLongPressTimerRef.current = null;
    }
    if (orderTitleLongPressTriggeredRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (orderTitleLongPressTimerRef.current) {
        clearTimeout(orderTitleLongPressTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      <header className="sticky top-0 z-40 select-none bg-white shadow-sm dark:bg-gray-900">
        <NewYearGarland enabled={newYearInterfaceEnabled} />
        <div className={`flex items-center px-4 ${newYearInterfaceEnabled ? 'sm:pt-2.5 sm:pb-2' : ''} py-2`}>
          <div className="flex items-center gap-2">
            {headerConfig.showMenu && (
              <button
                onClick={handleOpenSidebar}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg lg:hidden"
              >
                <Menu className="w-5 h-5 text-gray-700 dark:text-gray-200" />
              </button>
            )}
            {headerConfig.showBack && (
              <button
                onClick={handleBack}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300 hover:scale-105"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-200" />
              </button>
            )}
          </div>

          <div className="ml-2.5 flex min-w-0 flex-1 items-center gap-2">
            {location.pathname.startsWith('/order') ? (
              <button
                type="button"
                onMouseDown={handleOrderTitlePressStart}
                onMouseMove={handleOrderTitlePressMove}
                onMouseUp={handleOrderTitlePressEnd}
                onMouseLeave={handleOrderTitlePressEnd}
                onTouchStart={handleOrderTitlePressStart}
                onTouchMove={handleOrderTitlePressMove}
                onTouchEnd={handleOrderTitlePressEnd}
                onContextMenu={(e) => {
                  if (orderTitleLongPressTriggeredRef.current) {
                    e.preventDefault();
                  }
                }}
                className="truncate text-left font-bold text-base leading-tight text-gray-900 transition-opacity active:opacity-60 dark:text-gray-100 cursor-pointer"
                title="Удерживайте, чтобы скопировать номер заказа"
                aria-label={`${headerConfig.title} (удерживайте для копирования)`}
              >
                {headerConfig.title}
              </button>
            ) : (
              <span className="truncate font-bold text-base leading-tight text-gray-900 dark:text-gray-100">{headerConfig.title}</span>
            )}
            <div className="flex items-center gap-1.5 leading-none">
              {location.pathname.startsWith('/order') && orderHeaderRework && (
                <button
                  ref={registerOrderHeaderBadgeRef('rework')}
                  type="button"
                  onClick={() => handleOrderHeaderBadgeTap('rework')}
                  className={`${orderHeaderBadgeButtonClassName} border-amber-700/70 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-500/70 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-950/50`}
                  title="Доработка · высокий приоритет"
                  aria-label="Доработка · высокий приоритет"
                >
                  <RotateCcw className="h-3 w-3 text-amber-700 dark:text-amber-300" />
                </button>
              )}
              {location.pathname.startsWith('/order') && orderHeaderDispatcherCallStage && (
                <button
                  ref={registerOrderHeaderBadgeRef('call')}
                  type="button"
                  onClick={() => handleOrderHeaderBadgeTap('call')}
                  className={`${orderHeaderBadgeButtonClassName} border-sky-700/70 bg-sky-50 text-sky-800 hover:bg-sky-100 dark:border-sky-500/70 dark:bg-sky-950/30 dark:text-sky-200 dark:hover:bg-sky-950/50`}
                  title={orderHeaderDispatcherCallStage === 'pickup' ? 'Созвон перед забором' : 'Созвон перед доставкой'}
                  aria-label={orderHeaderDispatcherCallStage === 'pickup' ? 'Созвон перед забором' : 'Созвон перед доставкой'}
                >
                  <Phone className="h-3 w-3 text-sky-700 dark:text-sky-300" />
                </button>
              )}
              {location.pathname.startsWith('/order') && orderHeaderDispatcherPriority && (
                <button
                  ref={registerOrderHeaderBadgeRef('priority')}
                  type="button"
                  onClick={() => handleOrderHeaderBadgeTap('priority')}
                  className={`${orderHeaderBadgeButtonClassName} border-emerald-700/70 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-500/70 dark:bg-emerald-950/30 dark:text-emerald-200 dark:hover:bg-emerald-950/50`}
                  title="Высокий приоритет"
                  aria-label="Высокий приоритет"
                >
                  <ArrowUp className="h-3 w-3 text-emerald-700 dark:text-emerald-300" />
                </button>
              )}
              {location.pathname.startsWith('/order') && orderHeaderClientVip && (
                <button
                  ref={registerOrderHeaderBadgeRef('vip')}
                  type="button"
                  onClick={() => handleOrderHeaderBadgeTap('vip')}
                  className={`${orderHeaderBadgeButtonClassName} border-violet-700/70 bg-violet-50 text-violet-800 hover:bg-violet-100 dark:border-violet-500/70 dark:bg-violet-950/30 dark:text-violet-200 dark:hover:bg-violet-950/50`}
                  title="VIP клиент"
                  aria-label="VIP клиент"
                >
                  <Star className="h-3 w-3 fill-current text-violet-700 dark:text-violet-300" />
                </button>
              )}
              {location.pathname.startsWith('/order') && orderHeaderUrgent && (
                <button
                  ref={registerOrderHeaderBadgeRef('urgent')}
                  type="button"
                  onClick={() => handleOrderHeaderBadgeTap('urgent')}
                  className={`${orderHeaderBadgeButtonClassName} border-orange-700/70 bg-orange-50 text-orange-800 hover:bg-orange-100 dark:border-orange-500/70 dark:bg-orange-950/30 dark:text-orange-200 dark:hover:bg-orange-950/50`}
                  title="Срочный заказ"
                  aria-label="Срочный заказ"
                >
                  <Zap className="h-3 w-3 text-orange-700 dark:text-orange-300" />
                </button>
              )}
              {location.pathname.startsWith('/order') && orderHeaderEmergency && (
                <button
                  ref={registerOrderHeaderBadgeRef('emergency')}
                  type="button"
                  onClick={() => handleOrderHeaderBadgeTap('emergency')}
                  className={`${orderHeaderBadgeButtonClassName} border-fuchsia-700/70 bg-fuchsia-50 text-fuchsia-800 hover:bg-fuchsia-100 dark:border-fuchsia-500/70 dark:bg-fuchsia-950/30 dark:text-fuchsia-200`}
                  title="Экстренный выезд"
                  aria-label="Экстренный выезд"
                >
                  <Siren className="h-3 w-3 text-fuchsia-700 dark:text-fuchsia-300" />
                </button>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <OfflineIndicator />
            <button
              type="button"
              onClick={handleOrderLookupClick}
              className="relative rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Поиск заказа"
              title="Поиск заказа"
            >
              <Search className="h-5 w-5 text-gray-700 dark:text-gray-200" />
            </button>
            <button
              onClick={handleNotificationClick}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg relative"
            >
              <Bell className="w-5 h-5 text-gray-700 dark:text-gray-200" />
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-xs text-white font-bold">{unreadCount > 99 ? '99+' : unreadCount}</span>
                </div>
              )}
            </button>
          </div>
        </div>

        {headerMode && (
          <div className={`flex h-7 items-center justify-between gap-3 border-t border-gray-100 px-4 text-[11px] font-medium leading-none dark:border-gray-800 ${headerMode.surface || 'bg-gray-50 dark:bg-gray-800/60'} ${headerMode.tone || 'text-gray-700 dark:text-gray-200'}`}>
            <div className="flex min-w-0 items-center gap-1.5">
              {headerMode.icon ? <headerMode.icon className="h-3 w-3 shrink-0" /> : null}
              <span className="truncate">{headerMode.label}</span>
            </div>
            <button
              type="button"
              onClick={() => showToast(headerMode.description, 'info')}
              className={`inline-flex aspect-square h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-white/50 dark:hover:bg-black/10 ${headerMode.infoTone || 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'}`}
              aria-label="Пояснение режима"
              title="Пояснение режима"
            >
              <Info className="h-3 w-3" />
            </button>
          </div>
        )}
      </header>

      <NotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => {
          haptic('close');
          setIsNotificationModalOpen(false);
        }}
        onMarkAllAsRead={handleMarkAllAsRead}
        onNotificationRead={handleNotificationRead}
      />
      <OrderLookupModal
        open={isOrderLookupModalOpen}
        onValidationError={(message) => showToast(message, 'error', { duration: 2200 })}
        onClose={() => {
          haptic('close');
          setIsOrderLookupModalOpen(false);
        }}
      />
      {activeOrderHeaderBadgeHint && orderHeaderBadgeHintStyle && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="pointer-events-none fixed z-[70] -translate-x-1/2"
              style={orderHeaderBadgeHintStyle}
            >
              <div className="relative rounded-lg bg-gray-900 px-2 py-1 text-center text-[11px] font-medium leading-none text-white shadow-lg dark:bg-gray-100 dark:text-gray-900">
                {activeOrderHeaderBadgeHint === 'call'
                  ? (orderHeaderDispatcherCallStage === 'pickup' ? 'Созвон перед забором' : 'Созвон перед доставкой')
                  : activeOrderHeaderBadgeHint === 'rework'
                  ? 'Доработка · высокий приоритет'
                  : activeOrderHeaderBadgeHint === 'priority'
                  ? 'Высокий приоритет'
                  : activeOrderHeaderBadgeHint === 'emergency'
                    ? 'Экстренный выезд'
                  : activeOrderHeaderBadgeHint === 'vip'
                    ? 'VIP клиент'
                    : 'Срочный заказ'}
                <span className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-gray-900 dark:bg-gray-100" />
              </div>
            </div>,
            document.body
          )
        : null}
      <Toast toast={toast} onDismiss={hideToast} />
    </>
  );
};

export default Header;
